import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Input,
  Box,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FaLink, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useState, useCallback, useRef, useEffect } from 'react';
import { FileService } from '../services/FileService';
import { isMobileDevice } from '../utils/deviceDetection';

interface FileLink {
  url: string;
  status: 'checking' | 'accessible' | 'error';
  fileName?: string;
}

interface DirectFileLinkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: File[], urls: string[], shareableUrls: string[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onProgressChange?: (downloadedBytes: number, totalBytes: number, completedFiles?: number, totalFiles?: number) => void;
}


// Utility function to extract and clean filename from a URL
const extractFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const parts = pathname.split('/').filter(p => p);
    
    if (parts.length > 0) {
      let fileName = decodeURIComponent(parts[parts.length - 1]);
      // Remove query parameters if any
      const queryIndex = fileName.indexOf('?');
      if (queryIndex > 0) {
        fileName = fileName.substring(0, queryIndex);
      }
      
      // Try to clean up filenames that have hash prefixes (common in forum attachments)
      // Pattern: hash-description.extension -> description.extension
      // Example: 1173815d1765071821-walters-audio-secrettape-introduces-full-spectrum-magnetization-t805-clean.mp3
      //          -> clean.mp3 (if we can find a meaningful suffix)
      const hashPattern = /^[a-f0-9]+-.*?-(.+)$/i;
      const hashMatch = fileName.match(hashPattern);
      if (hashMatch && hashMatch[1]) {
        // Found a hash prefix, use the part after the last dash before the extension
        const cleaned = hashMatch[1];
        // Check if it's a reasonable filename (not too short, has extension)
        if (cleaned.length > 3 && cleaned.includes('.')) {
          fileName = cleaned;
        }
      }
      
      // Only return if it looks like a filename (has extension or reasonable length)
      if (fileName && fileName.length < 200 && fileName !== url) {
        return fileName;
      }
    }
  } catch {
    // URL parsing failed
  }
  
  // Fallback: try to extract from the URL string directly
  try {
    const lastSlash = url.lastIndexOf('/');
    if (lastSlash >= 0 && lastSlash < url.length - 1) {
      const potentialName = url.substring(lastSlash + 1);
      const queryIndex = potentialName.indexOf('?');
      let fileName = queryIndex > 0 ? potentialName.substring(0, queryIndex) : potentialName;
      fileName = decodeURIComponent(fileName);
      
      // Try to clean up hash prefixes
      const hashPattern = /^[a-f0-9]+-.*?-(.+)$/i;
      const hashMatch = fileName.match(hashPattern);
      if (hashMatch && hashMatch[1]) {
        const cleaned = hashMatch[1];
        if (cleaned.length > 3 && cleaned.includes('.')) {
          fileName = cleaned;
        }
      }
      
      if (fileName && fileName.length < 200 && fileName !== url) {
        return fileName;
      }
    }
  } catch {
    // Decoding failed
  }
  
  return 'audio-file';
};

const DirectFileLinkImportModal = ({
  isOpen,
  onClose,
  onFilesSelected,
  onLoadingChange,
  onProgressChange,
}: DirectFileLinkImportModalProps) => {
  const [urlInput, setUrlInput] = useState('');
  const [fileLinks, setFileLinks] = useState<FileLink[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadedBytes, setDownloadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const toast = useToast();
  
  // Notify parent of loading state changes
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:127',message:'Loading state changed',data:{isLoading,hasCallback:!!onLoadingChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // Notify parent of progress changes
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:133',message:'Progress changed',data:{downloadedBytes,totalBytes,completedFiles,totalFiles,hasCallback:!!onProgressChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    onProgressChange?.(downloadedBytes, totalBytes, completedFiles, totalFiles);
  }, [downloadedBytes, totalBytes, completedFiles, totalFiles, onProgressChange]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastItemRef = useRef<HTMLDivElement>(null);

  // Scroll the most recently added link into view
  useEffect(() => {
    if (lastItemRef.current && scrollContainerRef.current) {
      lastItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [fileLinks.length]);

  // Check if a URL is accessible and get the actual filename from headers
  const checkUrlAccessibility = useCallback(async (url: string): Promise<{ accessible: boolean; fileName?: string }> => {
    // Start with filename extracted from URL as fallback
    let fileName = extractFileNameFromUrl(url);

    // Check if URL has an audio file extension
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.opus'];
    const hasAudioExtension = fileName && audioExtensions.some(ext => 
      fileName!.toLowerCase().endsWith(ext)
    );

    // If it has an audio extension, mark as accessible immediately
    // CORS can block accessibility checks even for accessible files
    if (hasAudioExtension) {
      return { accessible: true, fileName };
    }

    // Try to verify accessibility and get actual filename from headers
    try {
      // Try GET with range header (works for most servers)
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Range': 'bytes=0-1023' },
        mode: 'cors',
      });

      if (response.ok || response.status === 206) {
        // Try to get filename from Content-Disposition header (most accurate)
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          // Try filename*=UTF-8''... format first (RFC 5987)
          let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
          if (match) {
            fileName = decodeURIComponent(match[1]);
          } else {
            // Try regular filename=... format
            match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
            if (match) {
              fileName = decodeURIComponent(match[1].trim());
            }
          }
        }
        
        // Check content type
        const contentType = response.headers.get('content-type');
        const isAudioType = contentType && contentType.startsWith('audio/');
        
        if (isAudioType) {
          return { accessible: true, fileName };
        }
      }
    } catch (error) {
      // CORS or other fetch errors - if URL has audio extension, assume accessible
      // Otherwise, we can't verify, so mark as accessible and let download handle it
      console.warn('Error checking URL accessibility:', error);
    }

    // If we can't verify but URL looks reasonable, mark as accessible
    // The actual download will handle any real errors
    return { accessible: true, fileName };
  }, []);

  const handleAddUrl = useCallback(async () => {
    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate URL format
    let urlToUse: string;
    try {
      new URL(trimmedUrl);
      urlToUse = trimmedUrl;
    } catch {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL format (e.g., https://example.com/file.mp3)',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if it's a Dropbox link and convert it to a direct download URL
    const isDropboxLink = urlToUse.includes('dropbox.com');
    if (isDropboxLink) {
      urlToUse = FileService.convertToDirectDownloadUrl(urlToUse);
    }

    // Check if it's a Google Drive link and convert it to a direct download URL
    const isGoogleDriveLink = urlToUse.includes('drive.google.com') || urlToUse.includes('googleusercontent.com');
    if (isGoogleDriveLink) {
      urlToUse = FileService.convertGoogleDriveToDirectDownloadUrl(urlToUse);
    }

    // Check if URL already exists (check both original and converted URLs)
    if (fileLinks.some(link => link.url === trimmedUrl || link.url === urlToUse)) {
      toast({
        title: 'Duplicate URL',
        description: 'This URL has already been added',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Add URL with checking status (store the converted URL)
    const newLink: FileLink = { url: urlToUse, status: 'checking' };
    setFileLinks(prev => [...prev, newLink]);
    setUrlInput('');

    // Check accessibility
    const { accessible, fileName } = await checkUrlAccessibility(urlToUse);
    
    setFileLinks(prev =>
      prev.map(link =>
        link.url === urlToUse
          ? { ...link, status: accessible ? 'accessible' : 'error', fileName }
          : link
      )
    );
  }, [urlInput, fileLinks, checkUrlAccessibility, toast]);

  const handleRemoveUrl = useCallback((url: string) => {
    setFileLinks(prev => prev.filter(link => link.url !== url));
  }, []);

  // Helper function to fetch file size from headers
  const fetchFileSize = async (url: string): Promise<number> => {
    // Strategy 1: Try HEAD request
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'cors' });
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const size = parseInt(contentLength, 10);
        if (size > 0) return size;
      }
    } catch (error) {
      // Continue to next strategy
    }

    // Strategy 2: Try Range request to get Content-Range header
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Range': 'bytes=0-1' },
        mode: 'cors',
      });
      
      if (response.status === 206) {
        // Partial content response - extract size from Content-Range header
        const contentRange = response.headers.get('content-range');
        if (contentRange) {
          const match = contentRange.match(/\/(\d+)$/);
          if (match) {
            const size = parseInt(match[1], 10);
            if (size > 0) return size;
          }
        }
      } else if (response.ok) {
        // Full response - check content-length
        const contentLength = response.headers.get('content-length');
        if (contentLength) {
          const size = parseInt(contentLength, 10);
          if (size > 0) return size;
        }
      }
    } catch (error) {
      // Continue to next strategy
    }

    // Strategy 3: Try CORS proxy HEAD request
    try {
      const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
      const response = await fetch(proxyUrl, { method: 'HEAD' });
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const size = parseInt(contentLength, 10);
        if (size > 0) return size;
      }
    } catch (error) {
      // Continue to next strategy
    }

    // Strategy 4: Try Range request through CORS proxy
    try {
      const proxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(url);
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: { 'Range': 'bytes=0-1' },
      });
      
      if (response.status === 206) {
        const contentRange = response.headers.get('content-range');
        if (contentRange) {
          const match = contentRange.match(/\/(\d+)$/);
          if (match) {
            const size = parseInt(match[1], 10);
            if (size > 0) return size;
          }
        }
      }
    } catch (error) {
      // All strategies failed
    }

    return 0; // Unknown size
  };

  const handleLoad = useCallback(async () => {
    const accessibleLinks = fileLinks.filter(link => link.status === 'accessible');
    
    if (accessibleLinks.length === 0) {
      toast({
        title: 'No accessible files',
        description: 'Please add at least one accessible file URL',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:294',message:'Starting file load',data:{fileCount:accessibleLinks.length,hasOnLoadingChange:!!onLoadingChange,hasOnProgressChange:!!onProgressChange},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    
    // Save accessible links before clearing state
    const linksToProcess = [...accessibleLinks];
    
    // Try to fetch file sizes before downloading to show total size estimate
    let estimatedTotalBytes = 0;
    const fileSizePromises = linksToProcess.map(link => fetchFileSize(link.url));
    const fileSizes = await Promise.all(fileSizePromises);
    const fileSizeMap = new Map<string, number>();
    fileSizes.forEach((size, index) => {
      if (size > 0) {
        estimatedTotalBytes += size;
        fileSizeMap.set(linksToProcess[index].url, size);
      }
    });
    
    // Set loading state BEFORE closing modal so spinner appears immediately
    // This ensures the loading state is set before any re-renders from modal closing
    // Set totalBytes to the estimated value (or 0 if we couldn't determine it) so percentage can show
    setIsLoading(true);
    setDownloadedBytes(0);
    setTotalBytes(estimatedTotalBytes); // Set to actual estimate, not 0
    setCompletedFiles(0);
    setTotalFiles(linksToProcess.length);
    
    // Close modal after loading state is set (like Dropbox picker)
    onClose();
    setFileLinks([]);
    setUrlInput('');
    
    try {
      // First pass: collect all filenames and blobs
      interface FileData {
        blob: Blob;
        fileName: string;
        url: string;
        shareableUrl: string;
      }
      const fileDataList: FileData[] = [];
      
      // Track progress per file
      const fileProgress = new Map<string, { downloaded: number; total: number }>();

      for (let index = 0; index < linksToProcess.length; index++) {
        const link = linksToProcess[index];
        // Update completed count to show we're starting this file (1-based index)
        setCompletedFiles(index + 1);
        
        try {
          // Try to fetch the file with multiple strategies
          let blob: Blob | null = null;
          let response: Response | null = null;

          // Strategy 1: Try to get filename from headers first (before downloading)
          let fileName = link.fileName || extractFileNameFromUrl(link.url);
          
          // Try to get Content-Disposition header from original URL first
          try {
            const headerResponse = await fetch(link.url, {
              method: 'HEAD',
              mode: 'cors',
            });
            if (headerResponse.ok) {
              const contentDisposition = headerResponse.headers.get('content-disposition');
              if (contentDisposition) {
                // Try filename*=UTF-8''... format first (RFC 5987)
                let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                if (match) {
                  fileName = decodeURIComponent(match[1]);
                } else {
                  // Try regular filename=... format
                  match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                  if (match) {
                    fileName = decodeURIComponent(match[1].trim());
                  }
                }
              }
            }
          } catch (headerError) {
            // If direct HEAD fails due to CORS, try HEAD through the CORS proxy
            try {
              const proxyHeaderUrl = 'https://corsproxy.io/?' + encodeURIComponent(link.url);
              const proxyHeaderResponse = await fetch(proxyHeaderUrl, {
                method: 'HEAD',
              });
              if (proxyHeaderResponse.ok) {
                const contentDisposition = proxyHeaderResponse.headers.get('content-disposition');
                if (contentDisposition) {
                  let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                  if (match) {
                    fileName = decodeURIComponent(match[1]);
                  } else {
                    match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                    if (match) {
                      fileName = decodeURIComponent(match[1].trim());
                    }
                  }
                }
              }
            } catch (proxyHeaderError) {
              // Try range request through proxy as fallback
              try {
                const proxyRangeUrl = 'https://corsproxy.io/?' + encodeURIComponent(link.url);
                const proxyRangeResponse = await fetch(proxyRangeUrl, {
                  method: 'GET',
                  headers: { 'Range': 'bytes=0-0' },
                });
                if (proxyRangeResponse.ok || proxyRangeResponse.status === 206) {
                  const contentDisposition = proxyRangeResponse.headers.get('content-disposition');
                  if (contentDisposition) {
                    let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                    if (match) {
                      fileName = decodeURIComponent(match[1]);
                    } else {
                      match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                      if (match) {
                        fileName = decodeURIComponent(match[1].trim());
                      }
                    }
                  }
                }
              } catch (proxyRangeError) {
                // Will use URL-extracted filename
              }
            }
          }
          
          try {
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:393',message:'Starting file fetch',data:{url:link.url},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'I'})}).catch(()=>{});
            // #endregion
            // Now fetch the actual file with progress tracking
            const result = await FileService.fetchWithMobileFallbackAndProgress(
              link.url,
              (downloaded, total) => {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:397',message:'File download progress',data:{downloaded,total,url:link.url},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'J'})}).catch(()=>{});
                // #endregion
                // Update individual file progress
                fileProgress.set(link.url, { downloaded, total });
                
                // Calculate cumulative downloaded bytes across ALL files
                let totalDownloaded = 0;
                
                // Sum up downloaded bytes from all files that have started
                linksToProcess.forEach(l => {
                  const progress = fileProgress.get(l.url);
                  if (progress && progress.downloaded > 0) {
                    // File is being downloaded, add its downloaded bytes
                    totalDownloaded += progress.downloaded;
                  }
                });
                
                // Update total downloaded bytes (cumulative across all files)
                setDownloadedBytes(totalDownloaded);
                
                // Update totalBytes only if we discover a larger actual size for this file
                // Use the larger of: estimated size or actual size discovered so far
                const currentTotal = fileProgress.get(link.url)?.total || 0;
                const estimatedSize = fileSizeMap.get(link.url) || 0;
                const actualSize = Math.max(currentTotal, estimatedSize);
                
                // Recalculate total size: sum of all file sizes (estimated or actual, whichever is larger)
                let recalculatedTotal = 0;
                linksToProcess.forEach(l => {
                  const progress = fileProgress.get(l.url);
                  const estSize = fileSizeMap.get(l.url) || 0;
                  if (progress && progress.total > 0) {
                    // Use actual discovered size if larger than estimate
                    recalculatedTotal += Math.max(progress.total, estSize);
                  } else {
                    // Use estimate for files not started yet
                    recalculatedTotal += estSize;
                  }
                });
                
                // Only update totalBytes if it's larger than what we had (never decrease it)
                if (recalculatedTotal > estimatedTotalBytes) {
                  setTotalBytes(recalculatedTotal);
                }
              }
            );
            blob = result.blob;
            response = result.response;
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:430',message:'File fetch completed',data:{blobSize:blob.size,url:link.url},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'K'})}).catch(()=>{});
            // #endregion
            
            // Ensure progress is set for this file (if not already set by progress callback)
            if (!fileProgress.has(link.url)) {
              fileProgress.set(link.url, { downloaded: blob.size, total: blob.size });
              // Update cumulative downloaded bytes
              let totalDownloaded = 0;
              linksToProcess.forEach(l => {
                const progress = fileProgress.get(l.url);
                if (progress && progress.downloaded > 0) {
                  totalDownloaded += progress.downloaded;
                }
              });
              setDownloadedBytes(totalDownloaded);
              
              // Update totalBytes if actual size is larger than estimate
              const actualSize = blob.size;
              const estimatedSize = fileSizeMap.get(link.url) || 0;
              if (actualSize > estimatedSize) {
                // Recalculate total: sum of all file sizes
                let recalculatedTotal = 0;
                linksToProcess.forEach(l => {
                  const progress = fileProgress.get(l.url);
                  const estSize = fileSizeMap.get(l.url) || 0;
                  if (progress && progress.total > 0) {
                    recalculatedTotal += Math.max(progress.total, estSize);
                  } else {
                    recalculatedTotal += estSize;
                  }
                });
                if (recalculatedTotal > estimatedTotalBytes) {
                  setTotalBytes(recalculatedTotal);
                }
              }
            }
            
            // Try to get filename from actual download response headers too (in case HEAD didn't work)
            if (response) {
              const contentDisposition = response.headers.get('content-disposition');
              if (contentDisposition) {
                let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                if (match) {
                  fileName = decodeURIComponent(match[1]);
                } else {
                  match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                  if (match) {
                    fileName = decodeURIComponent(match[1].trim());
                  }
                }
              }
            }
          } catch (directError) {
            // Strategy 2: Try CORS proxy (works on both mobile and desktop)
            try {
              const corsProxyUrl = 'https://corsproxy.io/?' + encodeURIComponent(link.url);
              
              // Try corsproxy.io with retry (sometimes 403s are transient)
              let proxyResponse: Response | null = null;
              for (let attempt = 0; attempt < 2; attempt++) {
                try {
                  if (attempt > 0) {
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second between retries
                  }
                  proxyResponse = await fetch(corsProxyUrl);
                  
                  if (proxyResponse.ok) {
                    break; // Success, exit retry loop
                  } else if (proxyResponse.status === 403 && attempt === 0) {
                    // 403 on first attempt, try once more
                    continue;
                  } else {
                    // Non-403 error or 403 on retry, break and try alternative
                    break;
                  }
                } catch (fetchError) {
                  if (attempt === 0) {
                    // First attempt failed, try once more
                    continue;
                  }
                  throw fetchError; // Second attempt also failed
                }
              }
              
              if (proxyResponse && proxyResponse.ok) {
                const proxyBlob = await proxyResponse.blob();
                if (proxyBlob.size > 1000) {
                  blob = proxyBlob;
                  response = proxyResponse;
                  
                  // Update progress for proxy download
                  fileProgress.set(link.url, { downloaded: proxyBlob.size, total: proxyBlob.size });
                  // Update cumulative downloaded bytes
                  let totalDownloaded = 0;
                  linksToProcess.forEach(l => {
                    const progress = fileProgress.get(l.url);
                    if (progress && progress.downloaded > 0) {
                      totalDownloaded += progress.downloaded;
                    }
                  });
                  setDownloadedBytes(totalDownloaded);
                  
                  // Update totalBytes if actual size is larger than estimate
                  const actualSize = proxyBlob.size;
                  const estimatedSize = fileSizeMap.get(link.url) || 0;
                  if (actualSize > estimatedSize) {
                    let recalculatedTotal = 0;
                    linksToProcess.forEach(l => {
                      const progress = fileProgress.get(l.url);
                      const estSize = fileSizeMap.get(l.url) || 0;
                      if (progress && progress.total > 0) {
                        recalculatedTotal += Math.max(progress.total, estSize);
                      } else {
                        recalculatedTotal += estSize;
                      }
                    });
                    if (recalculatedTotal > estimatedTotalBytes) {
                      setTotalBytes(recalculatedTotal);
                    }
                  }
                  
                  // Try to get filename from proxy response
                  const contentDisposition = proxyResponse.headers.get('content-disposition');
                  if (contentDisposition) {
                    let match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
                    if (match) {
                      fileName = decodeURIComponent(match[1]);
                    } else {
                      match = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
                      if (match) {
                        fileName = decodeURIComponent(match[1].trim());
                      }
                    }
                  }
                }
              } else if (proxyResponse) {
                // corsproxy.io returned non-OK, try alternative CORS proxy
                try {
                  // Try alternative: allorigins.win (another CORS proxy)
                  const altProxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(link.url)}`;
                  const altResponse = await fetch(altProxyUrl);
                  
                  if (altResponse.ok) {
                    const altBlob = await altResponse.blob();
                    if (altBlob.size > 1000) {
                      blob = altBlob;
                      response = altResponse;
                      
                      // Update progress for alternative proxy download
                      fileProgress.set(link.url, { downloaded: altBlob.size, total: altBlob.size });
                      // Update cumulative downloaded bytes
                      let totalDownloaded = 0;
                      linksToProcess.forEach(l => {
                        const progress = fileProgress.get(l.url);
                        if (progress && progress.downloaded > 0) {
                          totalDownloaded += progress.downloaded;
                        }
                      });
                      setDownloadedBytes(totalDownloaded);
                      
                      // Update totalBytes if actual size is larger than estimate
                      const actualSize = altBlob.size;
                      const estimatedSize = fileSizeMap.get(link.url) || 0;
                      if (actualSize > estimatedSize) {
                        let recalculatedTotal = 0;
                        linksToProcess.forEach(l => {
                          const progress = fileProgress.get(l.url);
                          const estSize = fileSizeMap.get(l.url) || 0;
                          if (progress && progress.total > 0) {
                            recalculatedTotal += Math.max(progress.total, estSize);
                          } else {
                            recalculatedTotal += estSize;
                          }
                        });
                        if (recalculatedTotal > estimatedTotalBytes) {
                          setTotalBytes(recalculatedTotal);
                        }
                      }
                    }
                  }
                } catch (altError) {
                  // Alternative proxy failed, continue to error
                }
              }
            } catch (proxyError) {
              // CORS proxy failed, continue to error
            }
            
            if (!blob) {
              throw new Error(`Failed to load file: ${directError instanceof Error ? directError.message : 'Network error'}`);
            }
          }

          if (!blob) {
            throw new Error('Failed to fetch file blob');
          }

          // Store file data for later processing
          fileDataList.push({
            blob,
            fileName,
            url: link.url,
            shareableUrl: link.url,
          });
          
          // Ensure progress is updated for this file if not already tracked
          if (!fileProgress.has(link.url)) {
            fileProgress.set(link.url, { downloaded: blob.size, total: blob.size });
            // Update cumulative downloaded bytes
            let totalDownloaded = 0;
            linksToProcess.forEach(l => {
              const progress = fileProgress.get(l.url);
              if (progress && progress.downloaded > 0) {
                totalDownloaded += progress.downloaded;
              }
            });
            setDownloadedBytes(totalDownloaded);
            
            // Update totalBytes if actual size is larger than estimate
            const actualSize = blob.size;
            const estimatedSize = fileSizeMap.get(link.url) || 0;
            if (actualSize > estimatedSize) {
              let recalculatedTotal = 0;
              linksToProcess.forEach(l => {
                const progress = fileProgress.get(l.url);
                const estSize = fileSizeMap.get(l.url) || 0;
                if (progress && progress.total > 0) {
                  recalculatedTotal += Math.max(progress.total, estSize);
                } else {
                  recalculatedTotal += estSize;
                }
              });
              if (recalculatedTotal > estimatedTotalBytes) {
                setTotalBytes(recalculatedTotal);
              }
            }
          }
          
          // File is now completed, but we already updated completedFiles when we started it
          // So no need to update again here
        } catch (error) {
          console.error(`Error loading file from ${link.url}:`, error);
          toast({
            title: 'Error loading file',
            description: `Failed to load file from ${link.url}. ${error instanceof Error ? error.message : 'Please check the URL is accessible.'}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }

      if (fileDataList.length > 0) {
        // Create File objects with original filenames
        // Simplification will be handled in App.tsx when files are added
        const files: File[] = [];
        const urls: string[] = [];
        const shareableUrls: string[] = [];
        
        fileDataList.forEach((fileData) => {
          const mimeType = FileService.inferMimeType(fileData.fileName) || 'audio/mpeg';
          const file = new File([fileData.blob], fileData.fileName, { type: mimeType });
          
          files.push(file);
          urls.push(fileData.url);
          shareableUrls.push(fileData.shareableUrl);
        });

        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/10a482d6-3b60-4d80-a61c-e7d18febb305',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DirectFileLinkImportModal.tsx:630',message:'Calling onFilesSelected',data:{fileCount:files.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run2',hypothesisId:'L'})}).catch(()=>{});
        // #endregion
        onFilesSelected(files, urls, shareableUrls);
        // Modal already closed, don't call onClose again
      } else {
        toast({
          title: 'No files loaded',
          description: 'Failed to load any files',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load files',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setDownloadedBytes(0);
      setTotalBytes(0);
      setCompletedFiles(0);
      setTotalFiles(0);
    }
  }, [fileLinks, onFilesSelected, onClose, toast]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setFileLinks([]);
      setUrlInput('');
      onClose();
    }
  }, [isLoading, onClose]);

  const accessibleCount = fileLinks.filter(link => link.status === 'accessible').length;
  const checkingCount = fileLinks.filter(link => link.status === 'checking').length;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: 'xl' }} isCentered>
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent bg="rgba(20, 20, 30, 0.95)" borderRadius="xl" p={4} mx={4} maxH="90vh" maxW={{ base: '100%', md: '600px' }}>
        <ModalHeader pb={2}>
          <HStack spacing={3}>
            <Icon as={FaLink} color="blue.400" />
            <Text fontSize="xl" fontWeight="bold">
              Import Direct File Links
            </Text>
          </HStack>
        </ModalHeader>

        <ModalBody py={4} overflowY="auto">
          <VStack spacing={4} align="stretch">
            <HStack spacing={2}>
              <Input
                placeholder="Add any public audio file link here"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddUrl();
                  }
                }}
                isDisabled={isLoading}
                bg="gray.800"
                borderColor="gray.600"
                _hover={{ borderColor: 'gray.500' }}
                _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
              />
              <Button
                onClick={handleAddUrl}
                isDisabled={isLoading || !urlInput.trim()}
                colorScheme="blue"
                minW="80px"
              >
                Add
              </Button>
            </HStack>

            {fileLinks.length > 0 && (
              <>
                <Divider />
                <Box ref={scrollContainerRef} maxH="300px" overflowY="auto" border="1px solid" borderColor="gray.600" borderRadius="md" p={2}>
                  <VStack spacing={2} align="stretch">
                    {fileLinks.map((link, index) => (
                      <HStack
                        key={index}
                        ref={index === fileLinks.length - 1 ? lastItemRef : undefined}
                        spacing={3}
                        p={2}
                        borderRadius="md"
                        bg={link.status === 'accessible' ? 'green.900' : link.status === 'error' ? 'red.900' : 'gray.800'}
                        border="1px solid"
                        borderColor={link.status === 'accessible' ? 'green.600' : link.status === 'error' ? 'red.600' : 'gray.600'}
                      >
                        {link.status === 'checking' && (
                          <Spinner size="sm" color="blue.400" />
                        )}
                        {link.status === 'accessible' && (
                          <Icon as={FaCheckCircle} color="green.400" />
                        )}
                        {link.status === 'error' && (
                          <Icon as={FaTimesCircle} color="red.400" />
                        )}
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" noOfLines={1} title={link.url} color="gray.200">
                            {link.fileName || link.url}
                          </Text>
                          {link.fileName && link.fileName !== link.url && (
                            <Text fontSize="xs" color="gray.400" noOfLines={1} title={link.url}>
                              {link.url}
                            </Text>
                          )}
                        </Box>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveUrl(link.url)}
                          isDisabled={isLoading}
                          colorScheme="red"
                        >
                          Remove
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  {accessibleCount} accessible {accessibleCount === 1 ? 'file' : 'files'}
                  {checkingCount > 0 && `, ${checkingCount} checking...`}
                </Text>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter pt={4}>
          <HStack spacing={3} width="100%">
            <Button
              variant="outline"
              onClick={handleClose}
              isDisabled={isLoading}
              flex={1}
              size="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleLoad}
              isDisabled={accessibleCount === 0 || isLoading || checkingCount > 0}
              isLoading={isLoading}
              flex={1}
              size="md"
            >
              Load {accessibleCount > 0 && `(${accessibleCount})`}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DirectFileLinkImportModal;

